import { Component, TemplateRef, ViewChild } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { Entity, SubAccount } from '@approot/models';
import { Header } from '@approot/models/ag-grid.model';
import { ReusableGridTemplateComponent } from '@approot/shared/custom-components/ag-grid/reusable-grid-template/reusable-grid-template.component';
import { TextBoxEditorComponent } from '@approot/shared/custom-components/ag-grid/text-box-editor/text-box-editor.component';
import { clone } from '@approot/utilities';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import {
  ColDef,
  GridApi,
  GridReadyEvent,
  ICellRendererParams
} from 'ag-grid-community';

@Component({
  selector: 'app-sub-accounts',
  templateUrl: './sub-accounts.component.html',
  styleUrls: ['./sub-accounts.component.scss']
})
export class SubAccountsComponent implements ICellRendererAngularComp {
  public entitiesForm: FormGroup;
  public rowIndex: number;
  public columnDefs: Header[];
  public selectedAccount: SubAccount;
  @ViewChild('inputComponent', { static: true })
  public inputComponent: TemplateRef<any>;
  public defaultColDef: ColDef;
  private gridApi: GridApi<SubAccount>;
  private subAccountToFormConverter: (
    entity: Partial<Entity>,
    headers: Header[]
  ) => FormGroup;

  public agInit(params: ICellRendererParams): void {
    this.rowIndex = params.data.rowIndex;
    this.entitiesForm = params.context.form;
    this.defaultColDef = params.context.defaultColDef;
    this.subAccountToFormConverter = params.context.subAccountToFormConverter;
    this.columnDefs = clone(params.context.headers);
    this.handleSubAccountsHeaders();
  }

  public refresh(params: ICellRendererParams): boolean {
    setTimeout(_ => {
      this.rowIndex = params.node.data.rowIndex;
      this.gridApi.refreshCells();
    }, 0);
    return true;
  }

  public onSelectionChanged(): void {
    this.selectedAccount = this.gridApi.getSelectedNodes()[0]?.data;
  }

  public gridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    const rowData = this.entitySubAccounts()
      .getRawValue()
      .map((x, index: number) => {
        return {
          ...x,
          rowIndex: index
        };
      });
    this.gridApi.setRowData(rowData);
  }

  public deleteEntity(): void {
    const rowData = this.gridApi.getSelectedNodes()[0].data;
    const rowIndex = rowData.rowIndex;
    this.gridApi.applyTransaction({
      remove: [rowData]
    });
    this.gridApi.forEachNode((x, index: number) => {
      x.updateData({ ...x.data, rowIndex: index });
    });

    setTimeout(() => {
      this.entitySubAccounts().removeAt(rowIndex);
      this.entitySubAccounts().markAsDirty();
      this.entitiesForm.markAsDirty();
    }, 0);
  }

  public addNewEntity(): void {
    const newSubAccountCtrl = this.subAccountToFormConverter(
      {},
      this.columnDefs
    );
    this.entitySubAccounts().push(newSubAccountCtrl);
    newSubAccountCtrl.setValidators([this.subAccountValidator]);
    const latestRowIndex = this.entitySubAccounts().length - 1;
    this.gridApi.applyTransaction({
      add: [
        {
          ...clone(newSubAccountCtrl.value),
          rowIndex: latestRowIndex
        }
      ]
    });
    this.entitiesForm.markAsDirty();
    newSubAccountCtrl.markAsDirty();
    this.gridApi.forEachNode(node => {
      if (node.data.rowIndex === latestRowIndex) {
        this.gridApi.ensureNodeVisible(node);
        return;
      }
    });
  }

  public entitySubAccounts(): FormArray {
    const entities = this.entitiesForm.get('entities') as FormArray;
    return entities.controls[this.rowIndex].get('subAccounts') as FormArray;
  }

  public updateRow(columnId: string, value: boolean, rowId: string): void {
    const row = this.gridApi.getRowNode(rowId);
    const rowDataClone = clone(row.data);
    rowDataClone[columnId] = value;
    row.updateData(rowDataClone);
  }

  private handleSubAccountsHeaders(): void {
    this.columnDefs.forEach(x => {
      if (x.valueFormatter === 'boolean') {
        x.cellRenderer = ReusableGridTemplateComponent;
        x.cellRendererParams = { ngTemplate: this.inputComponent };
        delete x.valueFormatter;
      } else {
        x.editable = true;
        x.cellEditorSelector = params => {
          return {
            component: TextBoxEditorComponent,
            params: {
              context: {
                form: this.entitySubAccounts().controls[params.data.rowIndex]
              }
            }
          };
        };
      }

      if (x.sortable) {
        x.comparator = customComparator;
      }
    });
  }

  private subAccountValidator(control: FormGroup): { InvalidRow: string } {
    return !Object.values(control.controls).every(x => x.value === '')
      ? null
      : { InvalidRow: 'Row has to have at least one value' };
  }
}

const customComparator = (valueA, valueB) => {
  return valueA
    ?.toString()
    .toLowerCase()
    .localeCompare(valueB?.toString().toLowerCase());
};
